import React, { useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';

import { AppDrawer } from '@layout/AppDrawer';
import { MainAppBar } from '@layout/MainAppBar';
import { drawerWidth, drawerBreakpoint } from '@root/app.config';
import { InformationBanner } from '@components/InformationBanner';
import { useDarkState } from '@store/appContext';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom/cjs/react-router-dom.min';

const useStyles = makeStyles((theme) => ({
  toolbar: theme.mixins.toolbar,
  content: {
    [theme.breakpoints.up(drawerBreakpoint)]: {
      width: `calc(100% - ${drawerWidth}px)`,
    },
    flex: '1 1 auto',
    maxWidth: '100%',
  },
  skipLink: {
    position: 'absolute',
    left: -999,
    top: '.5rem',
    color: theme.palette.primary.main,
    padding: '.5rem 1rem',
    borderRadius: 4,
    textDecoration: 'none',
    fontWeight: 600,
    zIndex: 2000,
    transition: 'left 0.2s',
    '&:focus': {
      left: '.5rem',
      outline: `2px solid ${theme.palette.primary.main}`,
    },
  },
}));

export const Layout = ({ children }) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const { isDark } = useDarkState();
  const mainRef = useRef(null);
  const location = useLocation();
  const prevPathname = useRef(null);

  // Update page title based on route
  useEffect(() => {
    const routeTitles = {
      '/': t('Cases'),
      '/create': t('Create sign request'),
      '/quicksign': t('QuickSign'),
      '/validate': t('Validate'),
      '/settings': t('Settings'),
    };

    // Match dynamic routes like /case/:id
    const getCaseTitle = () => {
      if (location.pathname.startsWith('/change/')) {
        return t('Change sign request');
      }
      if (location.pathname.startsWith('/sign/')) {
        return t('Sign request');
      }
      if (location.pathname.startsWith('/cases/')) {
        return t('Sign request');
      }
      return null;
    };

    const pageTitle = routeTitles[location.pathname] || getCaseTitle() || '';
    document.title = pageTitle ? `${pageTitle} | SignPort` : 'SignPort';

    return () => {
      document.title = 'SignPort';
    };
  }, [location.pathname, t]);

  useEffect(() => {
    // Only focus if pathname actually changed (not on initial mount)
    if (prevPathname.current !== null && prevPathname.current !== location.pathname) {
      mainRef.current?.focus();
    }
    prevPathname.current = location.pathname;
  }, [location.pathname]);

  return (
    <Box display="flex">
      <a
        href="#main-content"
        className={classes.skipLink}
        style={{
          background: isDark ? '#333333' : '#fff',
        }}
      >
        {t('SkipToMainContent')}
      </a>
      <MainAppBar />
      <AppDrawer />
      <main ref={mainRef} className={classes.content} tabIndex={-1} id="main-content">
        <div className={classes.toolbar} />
        <InformationBanner />
        {children}
      </main>
    </Box>
  );
};
